import React, { useState } from "react";

export default function UsersPanel({
    users,
    hasRole,
    groups,
    language,
    onUpdateGroups,
    pagination = {},
    onLoadUsers,
    isLoading = false,
}) {
    const [selectedUser, setSelectedUser] = useState(null);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [selectedUserGroup, setSelectedUserGroup] = useState("");
    const [groupAssignLoading, setGroupAssignLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // Use backend pagination structure
    const effectivePagination = {
        pageSize: pagination.pageSize || 10,
        nextCursor: pagination.nextCursor || null,
        prevCursor: pagination.prevCursor || null,
        hasNext: pagination.hasNext || false,
        hasPrev: pagination.hasPrev || false,
    };

    // Get current page users for display - always from backend
    const getCurrentPageUsers = () => {
        return users; // Backend pagination returns the current page users directly
    };

    const handleGroupAssign = async () => {
        if (selectedUser && onUpdateGroups) {
            setGroupAssignLoading(true);
            try {
                await onUpdateGroups(selectedUser.uid, selectedUserGroup);
                setShowGroupModal(false);
                setSelectedUser(null);
                setSelectedUserGroup("");
            } finally {
                setGroupAssignLoading(false);
            }
        }
    };

    const openGroupModal = (user) => {
        setSelectedUser(user);
        let initialGroup = user.groupId || "";
        setSelectedUserGroup(initialGroup);
        setShowGroupModal(true);
    };

    // Pagination handlers with loading states
    const handlePageChange = (direction) => {
        if (isLoading || !onLoadUsers) return;

        if (direction === "next" && effectivePagination.hasNext) {
            onLoadUsers(
                effectivePagination.pageSize,
                effectivePagination.nextCursor,
                searchQuery,
                "next",
            );
            // console.log(effectivePagination.nextCursor);
        } else if (direction === "prev" && effectivePagination.hasPrev) {
            onLoadUsers(
                effectivePagination.pageSize,
                effectivePagination.prevCursor,
                searchQuery,
                false, // resetPagination = false for previous page
                "prev",
            );
        }
    };

    const handlePageSizeChange = async (newPageSize) => {
        if (onLoadUsers && newPageSize !== effectivePagination.pageSize) {
            await onLoadUsers(newPageSize, null, searchQuery, true, "next"); // true = reset pagination
        }
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (onLoadUsers) {
            await onLoadUsers(
                effectivePagination.pageSize,
                null,
                searchQuery,
                true, // Reset pagination for search
                "next",
            );
        }
    };

    return (
        <div className="space-y-6">
            {/* Enhanced Search Section */}
            <div className="bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg
                                className="w-5 h-5 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {language === "zh" ? "用戶搜尋" : "User Search"}
                            </h3>
                            <p className="text-sm text-gray-600">
                                {language === "zh"
                                    ? "按電子郵件或姓名搜索用戶"
                                    : "Search users by email or name"}
                            </p>
                        </div>
                    </div>
                    <form
                        className="flex flex-row gap-3"
                        onSubmit={handleSearchSubmit}
                    >
                        <div className="flex-1">
                            <input
                                className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm"
                                value={searchQuery}
                                placeholder={
                                    language === "zh"
                                        ? "搜尋使用者（按電子郵件）"
                                        : "Search users (by email)"
                                }
                                onChange={(e) => {
                                    const query = e.target.value.trim();
                                    setSearchQuery(query);
                                }}
                            />
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                className="px-6 py-3 bg-blue-500 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-105 shadow-sm hover:shadow-md"
                            >
                                <div className="flex items-center gap-2">
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                    {language === "zh" ? "搜尋" : "Search"}
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchQuery("");
                                    if (onLoadUsers) {
                                        onLoadUsers(
                                            effectivePagination.pageSize,
                                            null,
                                            "",
                                            true, // reset pagination
                                        );
                                    }
                                }}
                                className="px-4 py-3 bg-orange-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all transform hover:scale-105 shadow-sm hover:shadow-md"
                            >
                                <div className="flex items-center gap-2">
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                    {language === "zh" ? "清除字段" : "Clear"}
                                </div>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Enhanced Pagination Controls - Top */}
            <div className="bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                                <svg
                                    className="w-4 h-4 text-indigo-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                    />
                                </svg>
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                                {language === "zh" ? "顯示" : "Show"}
                            </span>
                        </div>
                        <select
                            value={effectivePagination.pageSize}
                            onChange={(e) =>
                                handlePageSizeChange(Number(e.target.value))
                            }
                            disabled={isLoading}
                            className="border-2 border-gray-200 rounded-lg px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all bg-white shadow-sm"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                        <span className="text-sm font-medium text-gray-700">
                            {language === "zh" ? "條每頁" : "per page"}
                        </span>
                    </div>
                    {/* <div className="bg-indigo-50 px-4 py-2 rounded-lg border border-indigo-200">
                        <span className="text-sm font-medium text-indigo-800">
                            {language === "zh"
                                ? `顯示 ${
                                      (effectivePagination.currentPage - 1) *
                                          effectivePagination.pageSize +
                                      1
                                  } 到 ${Math.min(
                                      effectivePagination.currentPage *
                                          effectivePagination.pageSize,
                                      effectivePagination.totalUsers
                                  )} 共 ${effectivePagination.totalUsers} 條`
                                : `Showing ${
                                      (effectivePagination.currentPage - 1) *
                                          effectivePagination.pageSize +
                                      1
                                  } to ${
                                      (effectivePagination.currentPage - 1) *
                                          effectivePagination.pageSize +
                                      Math.min(
                                          effectivePagination.pageSize,
                                          effectivePagination.totalUsers
                                      )
                                  } of ${
                                      effectivePagination.totalUsers
                                  } entries`}
                        </span>
                    </div> */}
                </div>
            </div>

            {/* Enhanced Users Table */}
            <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden relative">
                {/* Loading overlay */}
                {isLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10 backdrop-blur-sm">
                        <div className="flex flex-col items-center space-y-4">
                            <div className="relative">
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
                            </div>
                            <p className="text-gray-600 text-sm font-medium">
                                {language === "zh"
                                    ? "加載用戶數據..."
                                    : "Loading user data..."}
                            </p>
                        </div>
                    </div>
                )}

                {/* Enhanced Header */}
                <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg
                                className="w-5 h-5 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                                />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">
                                {language === "zh"
                                    ? "用戶管理"
                                    : "User Management"}
                            </h3>
                            <p className="text-sm text-gray-600">
                                {language === "zh"
                                    ? "管理系統用戶和權限"
                                    : "Manage system users and permissions"}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === "zh" ? "用戶" : "User"}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === "zh" ? "用戶組" : "Groups"}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === "zh" ? "狀態" : "Status"}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === "zh" ? "操作" : "Actions"}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                // Loading state
                                <tr>
                                    <td
                                        colSpan="5"
                                        className="px-6 py-12 text-center"
                                    >
                                        <div className="flex flex-col items-center justify-center space-y-4">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                            <p className="text-gray-500 text-sm">
                                                {language === "zh"
                                                    ? "加載用戶..."
                                                    : "Loading users..."}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : getCurrentPageUsers().length === 0 ? (
                                // No users state
                                <tr>
                                    <td
                                        colSpan="5"
                                        className="px-6 py-12 text-center"
                                    >
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <svg
                                                className="h-12 w-12 text-gray-400"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                                                />
                                            </svg>
                                            <p className="text-gray-500 text-sm">
                                                {language === "zh"
                                                    ? "沒有找到用戶"
                                                    : "No users found"}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                // User rows
                                getCurrentPageUsers().map((user) => (
                                    <tr
                                        key={user.id}
                                        className="hover:bg-gray-50"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                                                        {(
                                                            user.displayName ||
                                                            user.name ||
                                                            user.email ||
                                                            "U"
                                                        )
                                                            .charAt(0)
                                                            .toUpperCase()}
                                                    </div>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {user.displayName ||
                                                            user.name ||
                                                            "No Name"}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {user.email ||
                                                            "No Email"}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-wrap gap-1">
                                                {/* Show groupId from your RBAC structure */}
                                                {user.groupId ? (
                                                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-800">
                                                        {user.groupId}
                                                    </span>
                                                ) : user.groups?.length > 0 ? (
                                                    // Fallback for old groups array structure
                                                    user.groups.map((group) => (
                                                        <span
                                                            key={group}
                                                            className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-800"
                                                        >
                                                            {group}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-500">
                                                        {language === "zh"
                                                            ? "無組"
                                                            : "No Group"}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                    user.status === "Active" ||
                                                    user.emailVerified
                                                        ? "bg-green-100 text-green-800"
                                                        : "bg-red-100 text-red-800"
                                                }`}
                                            >
                                                {user.status ||
                                                    (user.emailVerified
                                                        ? language === "zh"
                                                            ? "活躍"
                                                            : "Active"
                                                        : language === "zh"
                                                          ? "未驗證"
                                                          : "Unverified")}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() =>
                                                        openGroupModal(user)
                                                    }
                                                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-800 transition-all transform hover:scale-105 shadow-sm hover:shadow-md"
                                                >
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                                        />
                                                    </svg>
                                                    {language === "zh"
                                                        ? "更改用戶組/角色"
                                                        : "Change Group/Role"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Enhanced Pagination Controls - Bottom */}
            <div className="bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        {/* Previous Button */}
                        <button
                            onClick={() => handlePageChange("prev")}
                            disabled={!effectivePagination.hasPrev || isLoading}
                            className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105 ${
                                !effectivePagination.hasPrev || isLoading
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 shadow-sm hover:shadow-md"
                            }`}
                        >
                            {isLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                            ) : (
                                <>
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 19l-7-7 7-7"
                                        />
                                    </svg>
                                    <span>
                                        {language === "zh"
                                            ? "上一頁"
                                            : "Previous"}
                                    </span>
                                </>
                            )}
                        </button>

                        {/* Next Button */}
                        <button
                            onClick={() => handlePageChange("next")}
                            disabled={!effectivePagination.hasNext || isLoading}
                            className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105 ${
                                !effectivePagination.hasNext || isLoading
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 shadow-sm hover:shadow-md"
                            }`}
                        >
                            {isLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                            ) : (
                                <>
                                    <span>
                                        {language === "zh" ? "下一頁" : "Next"}
                                    </span>
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Enhanced Page info */}
                    {/* <div className="flex items-center space-x-2">
                        <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                            <span className="text-sm font-medium text-blue-800">
                                {language === "zh"
                                    ? `第 ${
                                          effectivePagination.currentPage
                                      } 頁${
                                          effectivePagination.hasNext
                                              ? " (還有更多)"
                                              : ""
                                      }`
                                    : `Page ${effectivePagination.currentPage}${
                                          effectivePagination.hasNext
                                              ? " (more available)"
                                              : ""
                                      }`}
                            </span>
                        </div> */}
                    {/* Navigation indicators */}
                    {/* <div className="flex items-center space-x-1 text-xs text-gray-500">
                            {effectivePagination.hasPrev && (
                                <span className="bg-gray-100 px-2 py-1 rounded">
                                    {language === "zh"
                                        ? "可前往上頁"
                                        : "Can go back"}
                                </span>
                            )}
                            {effectivePagination.hasNext && (
                                <span className="bg-gray-100 px-2 py-1 rounded">
                                    {language === "zh"
                                        ? "可前往下頁"
                                        : "More pages"}
                                </span>
                            )}
                        </div> */}
                    {/* </div> */}
                </div>
            </div>

            {/* Group Assignment Modal */}
            {showGroupModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowGroupModal(false)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                        {hasRole("users.assign_group") ? (
                            <>
                                {/* Header */}
                                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                                            <svg
                                                className="w-4 h-4 text-green-600"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                                />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900">
                                                {language === "zh"
                                                    ? "分配用戶組"
                                                    : "Assign Groups"}
                                            </h3>
                                            <p className="text-sm text-gray-500">
                                                {language === "zh"
                                                    ? "為用戶分配組和角色權限"
                                                    : "Assign group and role permissions to user"}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowGroupModal(false)}
                                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="p-6">
                                    <div className="mb-2">
                                        <label className="block text-sm font-semibold text-gray-900 mb-3">
                                            {language === "zh"
                                                ? "選擇用戶組"
                                                : "Select Group"}
                                        </label>
                                        <select
                                            value={selectedUserGroup || ""}
                                            onChange={(e) =>
                                                setSelectedUserGroup(
                                                    e.target.value,
                                                )
                                            }
                                            className="block w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-500/30 focus:border-green-500 transition-all bg-white"
                                        >
                                            <option value="" disabled>
                                                {language === "zh"
                                                    ? "請選擇"
                                                    : "Please select"}
                                            </option>
                                            {groups.map((group) => (
                                                <option
                                                    key={group.id}
                                                    value={group.id}
                                                >
                                                    {group.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                                    <button
                                        onClick={() => setShowGroupModal(false)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                                    >
                                        {language === "zh" ? "取消" : "Cancel"}
                                    </button>
                                    <button
                                        onClick={() => handleGroupAssign()}
                                        className={`px-5 py-2 rounded-xl text-sm font-medium transition-all ${
                                            groupAssignLoading
                                                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                                : "bg-green-500 hover:bg-green-600 text-white shadow-sm"
                                        }`}
                                        disabled={groupAssignLoading}
                                    >
                                        {groupAssignLoading ? (
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                                                {language === "zh"
                                                    ? "分配中..."
                                                    : "Assigning..."}
                                            </div>
                                        ) : (
                                            <>
                                                {language === "zh"
                                                    ? "分配"
                                                    : "Assign"}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Header */}
                                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                                            <svg
                                                className="w-4 h-4 text-red-600"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900">
                                                {language === "zh"
                                                    ? "訪問受限"
                                                    : "Access Restricted"}
                                            </h3>
                                            <p className="text-sm text-gray-500">
                                                {language === "zh"
                                                    ? "需要管理員權限"
                                                    : "Administrator privileges required"}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowGroupModal(false)}
                                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="p-8 text-center">
                                    <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <svg
                                            className="w-7 h-7 text-red-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636"
                                            />
                                        </svg>
                                    </div>
                                    <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                                        {language === "zh"
                                            ? "您沒有權限存取此部分。"
                                            : "You do not have permission to access this section."}
                                    </p>
                                    <button
                                        onClick={() => setShowGroupModal(false)}
                                        className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                                    >
                                        {language === "zh" ? "關閉" : "Close"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
